// The allow step of the machine editor's sign-in. Supabase sends you to
// /oauth/consent?authorization_id=…; the whole app opens there, logs you in the
// usual way, then asks this one question. Pure: the app makes the calls.

export const MACHINE_CONSENT_PATH = '/oauth/consent'
const MACHINE_CONSENT_STORAGE_KEY = 'lilo-machine-consent'

type ConsentStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
export type MachineConsentWindow = {
  location: { pathname: string, search: string }
  history: { replaceState: (data: unknown, unused: string, url: string) => void }
  sessionStorage: ConsentStorage
}

// loginOffered: we opened the login for this request already, so a reload doesn't nag.
export type MachineConsentRequest = { authorizationId: string, loginOffered: boolean }

export type MachineConsentFacts = {
  hasRequest: boolean
  isModalOpen: boolean
  isInFlight: boolean
  hasSyncAccess: boolean
  loginOffered: boolean
}

// wait: nothing to do yet. login: the usual login and subscription route.
// fetch: ask Supabase what this request is.
export type MachineConsentNext = 'wait' | 'login' | 'fetch'

export type MachineConsentStep =
  // Allowed before: Supabase answers with the way back, no question asked.
  | { kind: 'redirect', url: string }
  | { kind: 'ask', clientName: string, returnHost: string, email: string }
  // Expired, answered already, or another account's: nothing to approve.
  | { kind: 'expired' }

// The request outlives a trip to Stripe and back: the tab keeps it until it's answered.
export function captureMachineConsentRequest(win: MachineConsentWindow): MachineConsentRequest | undefined {
  const fromUrl = isConsentPath(win.location.pathname)
    ? new URLSearchParams(win.location.search).get('authorization_id')?.trim() || undefined
    : undefined
  const kept = readKeptRequest(win)
  // A reload of the same request keeps its state; a different one starts fresh.
  if (fromUrl && fromUrl !== kept?.authorizationId) {
    return keepMachineConsentRequest(win, { authorizationId: fromUrl, loginOffered: false })
  }
  return kept
}

export function keepMachineConsentRequest(win: MachineConsentWindow, request: MachineConsentRequest): MachineConsentRequest {
  try {
    win.sessionStorage.setItem(MACHINE_CONSENT_STORAGE_KEY, JSON.stringify(request))
  } catch {
    // Storage blocked: the request lasts for this page load only.
  }
  return request
}

export function releaseMachineConsentRequest(win: MachineConsentWindow): void {
  try {
    win.sessionStorage.removeItem(MACHINE_CONSENT_STORAGE_KEY)
  } catch {
    // Storage blocked: nothing was kept.
  }
  if (isConsentPath(win.location.pathname)) win.history.replaceState(null, '', '/')
}

// The allow modal waits its turn: it never covers another modal, and it sends you
// to the existing login once, so closing that login doesn't bring it back.
export function selectMachineConsentNext(facts: MachineConsentFacts): MachineConsentNext {
  if (!facts.hasRequest || facts.isModalOpen || facts.isInFlight) return 'wait'
  if (facts.hasSyncAccess) return 'fetch'
  return facts.loginOffered ? 'wait' : 'login'
}

// Reads Supabase's answer. Its type leaves out redirect_uri, the address the chat
// asked to return to, but the server sends it: that's the part a fake can't choose.
export function selectMachineConsentStep(data: unknown): MachineConsentStep {
  if (!isRecord(data)) return { kind: 'expired' }
  const redirect = webUrl(data.redirect_url)
  if (redirect) return { kind: 'redirect', url: redirect.href }
  const returnTo = webUrl(data.redirect_uri)
  const client = isRecord(data.client) ? data.client : {}
  const user = isRecord(data.user) ? data.user : {}
  if (!returnTo || typeof user.email !== 'string' || !user.email) return { kind: 'expired' }
  const clientName = typeof client.name === 'string' && client.name.trim() ? client.name.trim() : 'An unnamed app'
  return { kind: 'ask', clientName, returnHost: returnTo.host, email: user.email }
}

function readKeptRequest(win: MachineConsentWindow): MachineConsentRequest | undefined {
  try {
    const kept: unknown = JSON.parse(win.sessionStorage.getItem(MACHINE_CONSENT_STORAGE_KEY) ?? 'null')
    if (!isRecord(kept) || typeof kept.authorizationId !== 'string' || !kept.authorizationId) return undefined
    return { authorizationId: kept.authorizationId, loginOffered: kept.loginOffered === true }
  } catch {
    return undefined
  }
}

function isConsentPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === MACHINE_CONSENT_PATH
}

function webUrl(value: unknown): URL | undefined {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : undefined
  } catch {
    return undefined
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
